import type { BanDich } from '../../types';
import type { viRpaThongBao } from './thong-bao.vi';

export const enRpaThongBao: Pick<BanDich, keyof typeof viRpaThongBao> = {
  rpaThongBao: {
    phamVi: 'Applies to',
    phamViMacDinh: 'Default (all projects)',
    them: 'Add rule',
    tim: 'Search rule name or ID...',
    locSuKien: 'Filter by event',
    moiSuKien: 'All events',
    locKenh: 'Filter by channel',
    moiKenh: 'All channels',
    locTrangThai: 'Filter by status',
    moiTrangThai: 'All statuses',
    dem: '{{hien}} / {{tong}} rules',
    tongTrang: '{{tu}}–{{den}} of {{tong}} rules',
    goiYMacDinh:
      'Showing every notification rule. “All projects” rules apply to every project; a rule tied to a project applies only to that project.',
    goiYProject:
      'Notifications for {{project}}: the project’s own rules plus inherited shared rules. Turn off the switch on an inherited row to stop this project receiving messages from that rule.',
    rong: 'No notification rules yet',
    rongProject: 'This project has no notification rules yet',
    rongMoTa: 'Add a rule to get a message when a run fails or finishes.',
    rongLoc: 'No rules match the filters',
    cotTen: 'Rule name',
    cotSuKien: 'Event',
    cotPhamVi: 'Scope',
    cotKenh: 'Channels',
    cotBat: 'On',
    tatCaDuAn: 'All projects',
    riengProject: 'This project only',
    keThua: 'Inherited (all projects)',
    moiWorkflow: 'All workflows',
    workflowMot: 'Only workflow {{ten}}',
    workflowNhieu: '{{so}} workflows: {{ten}}, …',
    chuaCoBoGui: 'This event has no automatic sender yet',
    kenhCu: '{{kenh}} (legacy channel, not sent)',
    keThuaDangTat: 'Turned off in the shared rule',
    suaOMacDinh: 'Edit in Default view',
    batCauHinh: 'Turn on rule {{ten}}',
    daBat: 'Notification rule turned on.',
    daTat: 'Notification rule turned off.',
    projectDaBat: 'This project now receives notifications from the shared rule.',
    projectDaTat: 'This project will no longer receive notifications from the shared rule.',
    kenhLoi: 'Could not load channel configuration status: {{loi}}',
    sua: 'Edit rule',
    xoa: 'Delete rule',
    xoaTieuDe: 'Delete notification rule “{{ten}}”?',
    xoaThan: 'This rule will stop sending messages. This cannot be undone.',
    daXoa: 'Notification rule deleted.',
    suKien: {
      run_failed: 'Run failed',
      run_success: 'Run succeeded',
      all: 'All events',
      severity_critical: 'Critical errors',
      severity_high: 'High-severity errors',
      severity_medium: 'Medium-severity errors',
      severity_low: 'Low-severity errors',
      group_infra: 'Infrastructure errors',
      robot_offline: 'Robot offline',
    },
    kenhDich: {
      telegram: 'Telegram chat ID',
      email: 'Recipient emails',
      zalo: 'Recipient Zalo ID',
      whatsapp: 'WhatsApp phone number',
    },
    kenhMau: {
      telegram: '-1001234567890',
      email: 'ops@company.com, manager@company.com',
      zalo: 'Enter Zalo ID',
      whatsapp: '+84901234567',
    },
    saiDinhDang: {
      telegram: 'A chat ID is a number (groups start with -) or @channel_name.',
      email: 'Invalid email (up to 20 addresses, separated by commas).',
      zalo: 'A Zalo ID contains digits only.',
      whatsapp: 'Include the country code, for example +84901234567.',
    },
    khongCoTaiKhoan: {
      telegram: 'No enabled Telegram Bot account — add one in the Sender accounts tab to send via Telegram.',
      gmail: 'No enabled Gmail SMTP account — add one in the Sender accounts tab to send email.',
    },
    form: {
      taoTieuDe: 'Add notification rule',
      suaTieuDe: 'Edit rule “{{ten}}”',
      ten: 'Rule name',
      tenMau: 'For example: Gold price flow errors',
      suKien: 'Event',
      chuaCoBoGui: '{{ten}} (no automatic sender yet)',
      phamVi: 'Scope',
      workflow: 'Workflows to notify',
      workflowGoiY: 'Leave empty for every workflow in scope.',
      chonWorkflow: 'All workflows',
      workflowRong: 'No workflows in scope',
      workflowDaMat: 'No longer on OpenCore',
      thieuQuyenProject: 'Requires the {{quyen}} permission to pick projects and workflows.',
      kenh: 'Channels',
      kenhBatBuoc: 'Select at least one channel.',
      batBuoc: 'Required.',
      daCauHinh: 'Sender account ready',
      chuaCauHinh: 'No sender account',
      guiThu: 'Send test message',
      dangGuiThu: 'Sending...',
      daGuiThu: 'Test message sent via {{kenh}}.',
      taiKhoan: 'Sender account',
      taiKhoanMacDinh: 'Default ({{ten}})',
      bat: 'Turn on this rule',
      them: 'Add rule',
      luu: 'Save rule',
      daTao: 'Notification rule added.',
      daLuu: 'Notification rule saved.',
    },
  },
  rpaTaiKhoanGui: {
    maHoaKieu: { starttls: 'STARTTLS (port 587)', ssl: 'SSL/TLS (port 465)', none: 'No encryption' },
    moTa:
      'Accounts the RPA server uses to send error and run-result notifications. Rules use each type’s default account unless they pick one.',
    them: 'Add account',
    tim: 'Search account name or email...',
    locLoai: 'Filter by type',
    moiLoai: 'All types',
    dem: '{{hien}} / {{tong}} accounts',
    tongTrang: '{{tu}}–{{den}} of {{tong}} accounts',
    rong: 'No sender accounts yet',
    rongMoTa: 'Add a Gmail SMTP account to send email notifications, or a Telegram Bot to send via Telegram.',
    rongLoc: 'No accounts match the filters',
    cotTen: 'Account name',
    cotLoai: 'Type',
    cotGuiTu: 'Sends from',
    cotMacDinh: 'Default',
    cotBat: 'On',
    capNhat: 'Updated {{luc}}',
    moiWorkflow: 'Sends for every workflow',
    gioiHanWorkflow: 'Sends for {{so}} workflows only',
    botDaLuu: 'Bot token saved',
    botChuaCo: 'No bot token',
    datMacDinh: 'Make “{{ten}}” the default',
    laMacDinh: 'Default account',
    batTaiKhoan: 'Turn on account {{ten}}',
    daDatMacDinh: '“{{ten}}” is now the default.',
    daBat: 'Sender account turned on.',
    daTat: 'Sender account turned off.',
    sua: 'Edit account',
    xoa: 'Delete account',
    xoaTieuDe: 'Delete sender account “{{ten}}”?',
    xoaThan: 'Rules that picked this account will switch to the default account. This cannot be undone.',
    daXoa: 'Sender account deleted.',
    chanTrang: 'App passwords and bot tokens are kept in the server’s secret store and are not shown again after saving.',
    loai: 'Account type',
    loaiKhoa: 'The type of an existing account cannot change; create a new account for another type.',
    gmail: 'Gmail SMTP',
    gmailMoTa: 'Send email through Gmail',
    telegram: 'Telegram Bot',
    telegramMoTa: 'Send messages through a Telegram bot',
    form: {
      taoTieuDe: 'Add sender account',
      suaTieuDe: 'Edit account “{{ten}}”',
      ten: 'Account name',
      tenMau: 'Native system email',
      email: 'Sender email',
      emailMau: 'noreply@company.com',
      appPassword: 'App password',
      appPasswordGoiY: 'Create one under Google Account > Security > App passwords (2-step verification required).',
      giuBiMat: 'Leave empty to keep the current one.',
      smtpHost: 'SMTP host',
      smtpPort: 'Port',
      maHoa: 'Encryption',
        botToken: 'Bot token',
      botTokenGoiY: 'Get it from @BotFather on Telegram.',
      guiThuToiMail: 'Send test to',
      guiThuToiMailGoiY: 'Leave empty to send to the sender email itself.',
      guiThuToiTelegram: 'Test chat ID',
      guiThuToiTelegramGoiY: 'Required to send a test message.',
      workflow: 'Workflows allowed to send',
      workflowGoiY:
        'Leave empty for every workflow. When set, only runs of these workflows send through this account, even when the robot picks the channel.',
      macDinh: 'Make default',
      macDinhGoiY: 'Rules that do not pick an account send through this one.',
      macDinhPhaiBat: 'The default account must be turned on.',
      bat: 'Turn on account',
      batGoiY: 'A turned-off account is not used to send.',
      guiMailThu: 'Send test email',
      guiTinThu: 'Send test message',
      dangGuiThu: 'Sending...',
      daGuiMailThu: 'Test email sent to {{toi}}.',
      daGuiTinThu: 'Test message sent.',
      canChatId: 'Enter a chat ID for the test message.',
      them: 'Add account',
      luu: 'Save account',
      daThem: 'Sender account added.',
      daLuu: 'Sender account saved.',
      batBuoc: 'Required.',
      emailSai: 'Invalid sender email.',
      hostSai: 'The SMTP host may contain only letters, digits, dots and hyphens.',
      portSai: 'The port must be a number from 1 to 65535.',
      tokenSai: 'Invalid bot token format (looks like 123456:ABC…).',
    },
  },
};
